import { PlusCircleIcon, TrashIcon, XCircleIcon } from '@phosphor-icons/react';
import { Input } from 'antd';
import { useId, useState } from 'react';

import { Button } from '@/components/common/button';

import {
  COORDINATE_HELPER_TEXT,
  type CoordinateAxis,
  type CoordinateRow,
  type RowErrors,
} from '../coordinates';

import styles from './coordinate-rows.module.scss';

export type CoordinateRowsProps = {
  rows: readonly CoordinateRow[];
  /** Same length and order as `rows`; an absent axis means that cell is not in error. */
  errors: readonly RowErrors[];
  canAddRow: boolean;
  /** Whether ANY row may be deleted — false at exactly the minimum, where the control is **absent**
   *  rather than greyed (grilling Q7 / D7). One boolean, not one per row: the rule is the count. */
  canDeleteRow: boolean;
  canSave: boolean;
  onValueChange: (index: number, axis: CoordinateAxis, raw: string) => void;
  onCellBlur: (index: number, axis: CoordinateAxis) => void;
  onAddRow: () => void;
  onDeleteRow: (index: number) => void;
  onCancel: () => void;
  onSave: () => void;
};

const AXIS_PLACEHOLDER: Record<CoordinateAxis, string> = { lat: 'Lat', lon: 'Long' };
const AXIS_NAME: Record<CoordinateAxis, string> = { lat: 'latitude', lon: 'longitude' };

type CellProps = {
  axis: CoordinateAxis;
  pointNumber: number;
  value: string;
  error?: string;
  onChange: (raw: string) => void;
  onBlur: () => void;
};

/**
 * One Lat or Long input with its own inline error. Module-local rather than exported: two of these
 * appear per row and nowhere else, so it is deduplication, not a component the library needs.
 *
 * The chrome is the shipped antd field theme (`src/config/theme.ts` — 40px, radius 10,
 * `Border/Subtle`, `Error/900` when in error), so nothing about the box is drawn here. `allowClear`
 * IS the criterion's clear (×): antd renders it only when the input is populated.
 */
function Cell({ axis, pointNumber, value, error, onChange, onBlur }: CellProps) {
  const errorId = useId();

  return (
    <div className={styles.cell}>
      <Input
        aria-label={`Point ${pointNumber} ${AXIS_NAME[axis]}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        status={error ? 'error' : undefined}
        placeholder={AXIS_PLACEHOLDER[axis]}
        value={value}
        allowClear={{ clearIcon: <XCircleIcon size={16} /> }}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The Field Mapping panel's **Co-ordinates** section — the second way to define a Boundary
 * (Figma `594:532574` header, `594:533213` a row, `594:532922` a row in error, `594:532618` the
 * foot).
 *
 * **Presentational by construction.** It holds no DOMAIN state — the draft has exactly one home
 * (the page, through `useCoordinateDraft`) and this component cannot disagree with the canvas about
 * what is typed; every rule — stripping, ranges, duplicates, the Save gate — lives above it. It
 * does hold one local, purely-rendering concern of its own: a stable id per row, used only for
 * React's `key` so a delete does not steal focus from whatever the Grower is typing in (see
 * `rowIds` below) — `rows` itself stays exactly what the props say.
 *
 * `Save` is `aria-disabled`, never `disabled`: pressing it while a cell is blank has to flag the
 * blanks, and a disabled control dispatches no click. The same call `Save Mapping` already makes.
 */
export function CoordinateRows({
  rows,
  errors,
  canAddRow,
  canDeleteRow,
  canSave,
  onValueChange,
  onCellBlur,
  onAddRow,
  onDeleteRow,
  onCancel,
  onSave,
}: CoordinateRowsProps) {
  const headingId = useId();

  // A stable id per CURRENT row, generated and owned entirely here — `CoordinateRow` itself
  // carries no identity (`../coordinates.ts` and `use-coordinate-draft.ts` are out of scope; see
  // the ticket). Keying `<li>` by `index` re-keys every row below a delete: the array shrinks, so
  // the LAST key — exactly the one holding whatever the Grower is focused in — always unmounts,
  // regardless of where the delete happened, stealing focus outright.
  //
  // `useState`, not a ref: this value has to flow into JSX, and reading `ref.current` during
  // render — even from inside a `useMemo` used only to compute the key — is flagged by
  // `react-hooks/refs` ("Cannot access refs during render"). State is the render-safe home for it,
  // and every WRITE happens in an event handler, never in the render body or an effect
  // (`react-hooks/set-state-in-render`, `react-hooks/set-state-in-effect`, `react-hooks/purity` —
  // `crypto.randomUUID()` is non-deterministic and may not run during render).
  //
  // The two mutations this component ever triggers are well-behaved (`use-coordinate-draft.ts`'s
  // `addRow` always appends one row at the end, `deleteRow` always splices one known index), so
  // `handleAddRow` / `handleDeleteRow` below keep `rowIds` in lockstep with `rows` synchronously,
  // in the SAME handler that calls the prop — before the parent's state update (and this
  // component's next render with the new `rows`) ever lands.
  //
  // KNOWN GAP, not fixed here: a row count that changes some OTHER way keeps `rowIds` at its old
  // length, so the `?? index` fallback below covers it (no crash, no duplicate key) but does not
  // preserve focus through a later delete. Cancel/Undo genuinely don't need to — the whole draft
  // resets there, so there is no row identity worth preserving across it. Marking a **Point** on
  // the canvas while the draft is clean is different: `rows` grows to match `points` with no call
  // through this component at all (`use-coordinate-draft.ts`'s derived-rows trick), so a row added
  // that way is untracked, and deleting an earlier row afterward can still steal its focus. This is
  // a real, live residual — not an inert one — left open because closing it needs `rowIds` to grow
  // in response to a PROP change rather than a local event, which this component could not do
  // without either reading a ref during render (`react-hooks/refs`) or writing state from an effect
  // (`react-hooks/set-state-in-effect`); see ticket 09 for the follow-up.
  const [rowIds, setRowIds] = useState<string[]>(() => rows.map(() => crypto.randomUUID()));

  const handleAddRow = () => {
    setRowIds((current) => [...current, crypto.randomUUID()]);
    onAddRow();
  };

  // Splices the id out at the SAME index, synchronously, before `onDeleteRow` fires — so by the
  // time the parent's state update lands and this component re-renders with the shorter `rows`,
  // `rowIds` is already in lockstep.
  const handleDeleteRow = (index: number) => {
    setRowIds((current) => current.toSpliced(index, 1));
    onDeleteRow(index);
  };

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.header}>
        <div className={styles.headingRow}>
          <p id={headingId} className={styles.heading}>
            Co-ordinates
          </p>
          <button
            type="button"
            className={styles.addPoint}
            disabled={!canAddRow}
            onClick={handleAddRow}
          >
            <PlusCircleIcon size={16} aria-hidden />
            Add point
          </button>
        </div>
        <p className={styles.helper}>{COORDINATE_HELPER_TEXT}</p>
      </div>

      {/* An ordered list because the order IS the shape: the Points are plotted in row order. */}
      <ol className={styles.rows}>
        {rows.map((row, index) => (
          // Keyed by the row's own stable id (`rowIds`), NOT `index` — the number a row wears is
          // its position and renumbers on a delete, but the DOM node (and whatever focus it holds)
          // must follow the row, not the position.
          <li key={rowIds[index] ?? index} className={styles.row}>
            <p className={styles.pointLabel}>{`Point ${index + 1}`}</p>
            <div className={styles.inputs}>
              {(['lat', 'lon'] as const).map((axis) => (
                <Cell
                  key={axis}
                  axis={axis}
                  pointNumber={index + 1}
                  value={row[axis]}
                  error={errors[index]?.[axis]}
                  onChange={(raw) => onValueChange(index, axis, raw)}
                  onBlur={() => onCellBlur(index, axis)}
                />
              ))}
              {canDeleteRow ? (
                <button
                  type="button"
                  className={styles.delete}
                  aria-label={`Delete point ${index + 1}`}
                  onClick={() => handleDeleteRow(index)}
                >
                  <TrashIcon size={20} aria-hidden />
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ol>

      <div className={styles.foot}>
        <Button variant="outline" size="small" className={styles.action} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="neutral"
          size="small"
          className={styles.action}
          aria-disabled={!canSave}
          onClick={onSave}
        >
          Save
        </Button>
      </div>
    </section>
  );
}
